import { Component, ChangeDetectionStrategy, input, output, computed, inject } from '@angular/core';
import { Router, RouterModule } from '@angular/router';

/**
 * Button variants available in the design system
 * - primary: Solid primary color button (default)
 * - secondary: Solid secondary/gray button
 * - success: Solid green success button
 * - danger: Solid red danger/error button
 * - warning: Solid amber/orange warning button
 * - info: Solid blue info button
 * - gradient: Gradient from indigo to purple (premium look)
 * - gradient-blue: Blue gradient (matches blue page header)
 * - gradient-green: Green gradient (matches green page header)
 * - gradient-purple: Purple gradient (matches purple page header)
 * - gradient-red: Red gradient (matches red page header)
 * - gradient-orange: Orange gradient (matches orange page header)
 * - gradient-indigo: Indigo gradient (matches indigo page header)
 * - white: White button for use in headers
 * - white-blue: White with blue gradient text (for blue headers)
 * - white-green: White with green gradient text (for green headers)
 * - white-purple: White with purple gradient text (for purple headers)
 * - white-red: White with red gradient text (for red headers)
 * - white-orange: White with orange gradient text (for orange headers)
 * - white-indigo: White with indigo gradient text (for indigo headers)
 * - outline-primary: Outlined primary button
 * - outline-secondary: Outlined secondary button
 * - ghost: Transparent with hover effect
 * - link: Text-only link style
 */
export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'danger'
  | 'warning'
  | 'info'
  | 'gradient'
  | 'gradient-blue'
  | 'gradient-green'
  | 'gradient-purple'
  | 'gradient-red'
  | 'gradient-orange'
  | 'gradient-indigo'
  | 'white'
  | 'white-blue'
  | 'white-green'
  | 'white-purple'
  | 'white-red'
  | 'white-orange'
  | 'white-indigo'
  | 'outline-primary'
  | 'outline-secondary'
  | 'ghost'
  | 'link';

export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type ButtonType = 'button' | 'submit' | 'reset';

@Component({
  selector: 'app-atom-button',
  standalone: true,
  imports: [RouterModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './button.component.html',
  styleUrls: ['./button.component.scss'],
})
export class ButtonComponent {
  private readonly router = inject(Router);

  // Using Angular 19 signal-based inputs
  variant = input<ButtonVariant>('primary');
  size = input<ButtonSize>('md');
  disabled = input<boolean>(false);
  loading = input<boolean>(false);
  type = input<ButtonType>('button');
  fullWidth = input<boolean>(false);
  additionalClasses = input<string>('');
  icon = input<string>('');
  iconPosition = input<'left' | 'right'>('left');

  // Navigation support
  routerLink = input<string | any[] | null>(null);
  queryParams = input<Record<string, string> | null>(null);

  // Signal-based output
  componentClick = output<void>();

  // Computed gradient text classes for white buttons
  gradientTextClasses = computed(() => {
    const variant = this.variant();
    const gradientMap: Record<string, string> = {
      'white-blue': 'bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent',
      'white-green': 'bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent',
      'white-purple': 'bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent',
      'white-red': 'bg-gradient-to-r from-red-600 to-rose-600 bg-clip-text text-transparent',
      'white-orange': 'bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent',
      'white-indigo': 'bg-gradient-to-r from-indigo-600 to-blue-600 bg-clip-text text-transparent',
    };
    return gradientMap[variant] || '';
  });

  // Check if this is a white gradient button
  isWhiteGradient = computed(() => {
    return this.variant().startsWith('white-') && this.variant() !== 'white';
  });

  // Computed class getter using signals
  buttonClasses = computed(() => {
    const classes: string[] = [];

    // Variant classes - comprehensive mapping
    const variantMap: Record<ButtonVariant, string> = {
      primary:
        'bg-primary-500 text-white hover:bg-primary-600 active:bg-primary-700 focus:ring-primary-500 shadow-sm',
      secondary:
        'bg-secondary-500 text-white hover:bg-secondary-600 active:bg-secondary-700 focus:ring-secondary-500 shadow-sm',
      success:
        'bg-success-500 text-white hover:bg-success-600 active:bg-success-700 focus:ring-success-500 shadow-sm',
      danger:
        'bg-danger-500 text-white hover:bg-danger-600 active:bg-danger-700 focus:ring-danger-500 shadow-sm',
      warning:
        'bg-warning-500 text-white hover:bg-warning-600 active:bg-warning-700 focus:ring-warning-500 shadow-sm',
      info: 'bg-blue-500 text-white hover:bg-blue-600 active:bg-blue-700 focus:ring-blue-500 shadow-sm',

      // Default gradient (indigo-purple)
      gradient:
        'bg-gradient-to-r from-indigo-500 to-purple-600 text-white hover:from-indigo-600 hover:to-purple-700 active:from-indigo-700 active:to-purple-800 focus:ring-indigo-500 shadow-md',

      // Color-matched gradients for page headers
      'gradient-blue':
        'bg-gradient-to-r from-blue-500 to-cyan-600 text-white hover:from-blue-600 hover:to-cyan-700 active:from-blue-700 active:to-cyan-800 focus:ring-blue-500 shadow-md',
      'gradient-green':
        'bg-gradient-to-r from-emerald-500 to-teal-600 text-white hover:from-emerald-600 hover:to-teal-700 active:from-emerald-700 active:to-teal-800 focus:ring-emerald-500 shadow-md',
      'gradient-purple':
        'bg-gradient-to-r from-purple-500 to-pink-600 text-white hover:from-purple-600 hover:to-pink-700 active:from-purple-700 active:to-pink-800 focus:ring-purple-500 shadow-md',
      'gradient-red':
        'bg-gradient-to-r from-red-500 to-rose-600 text-white hover:from-red-600 hover:to-rose-700 active:from-red-700 active:to-rose-800 focus:ring-red-500 shadow-md',
      'gradient-orange':
        'bg-gradient-to-r from-orange-500 to-amber-600 text-white hover:from-orange-600 hover:to-amber-700 active:from-orange-700 active:to-amber-800 focus:ring-orange-500 shadow-md',
      'gradient-indigo':
        'bg-gradient-to-r from-indigo-500 to-blue-600 text-white hover:from-indigo-600 hover:to-blue-700 active:from-indigo-700 active:to-blue-800 focus:ring-indigo-500 shadow-md',

      // White buttons for headers (plain white)
      white:
        'bg-white/95 text-gray-900 hover:bg-white active:bg-gray-50 focus:ring-white shadow-lg backdrop-blur-sm font-semibold',

      // White buttons with gradient text (for use in colored headers)
      'white-blue':
        'bg-white/95 hover:bg-white active:bg-gray-50 shadow-lg backdrop-blur-sm font-semibold border border-white/50',
      'white-green':
        'bg-white/95 hover:bg-white active:bg-gray-50 shadow-lg backdrop-blur-sm font-semibold border border-white/50',
      'white-purple':
        'bg-white/95 hover:bg-white active:bg-gray-50 shadow-lg backdrop-blur-sm font-semibold border border-white/50',
      'white-red':
        'bg-white/95 hover:bg-white active:bg-gray-50 shadow-lg backdrop-blur-sm font-semibold border border-white/50',
      'white-orange':
        'bg-white/95 hover:bg-white active:bg-gray-50 shadow-lg backdrop-blur-sm font-semibold border border-white/50',
      'white-indigo':
        'bg-white/95 hover:bg-white active:bg-gray-50 shadow-lg backdrop-blur-sm font-semibold border border-white/50',

      // Outlined buttons
      'outline-primary':
        'border-2 border-primary-500 text-primary-600 bg-transparent hover:bg-primary-50 active:bg-primary-100 focus:ring-primary-500',
      'outline-secondary':
        'border-2 border-secondary-300 text-secondary-600 bg-transparent hover:bg-secondary-50 active:bg-secondary-100 focus:ring-secondary-500',
      ghost:
        'text-secondary-600 bg-transparent hover:bg-secondary-100 active:bg-secondary-200 focus:ring-secondary-500',
      link: 'text-primary-600 hover:text-primary-700 hover:underline focus:ring-primary-500 !p-0 !shadow-none',
    };

    classes.push(variantMap[this.variant()]);

    // Size classes - consistent padding and font sizes
    const sizeMap: Record<ButtonSize, string> = {
      xs: 'px-2.5 py-1 text-xs',
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-4 py-2 text-sm',
      lg: 'px-5 py-2.5 text-base',
      xl: 'px-6 py-3 text-lg',
    };

    classes.push(sizeMap[this.size()]);

    // Common classes for all buttons
    classes.push(
      'inline-flex items-center justify-center gap-2',
      'rounded-lg font-semibold',
      'whitespace-nowrap',
      'transition-all duration-200 ease-in-out',
      'focus:outline-none focus:ring-2 focus:ring-offset-2',
      'select-none'
    );

    // Full width
    if (this.fullWidth()) {
      classes.push('w-full');
    }

    // Disabled/loading state
    if (this.disabled() || this.loading()) {
      classes.push('opacity-50 cursor-not-allowed pointer-events-none');
    } else {
      classes.push('cursor-pointer');
    }

    // Additional classes (for overrides)
    if (this.additionalClasses()) {
      classes.push(this.additionalClasses());
    }

    return classes.join(' ');
  });

  handleClick(event: MouseEvent): void {
    event.stopPropagation();
    if (!this.disabled() && !this.loading()) {
      // Handle navigation if routerLink is provided
      const link = this.routerLink();
      if (link) {
        const params = this.queryParams();
        if (params) {
          this.router.navigate(Array.isArray(link) ? link : [link], { queryParams: params });
        } else {
          this.router.navigate(Array.isArray(link) ? link : [link]);
        }
      }
      this.componentClick.emit();
    }
  }
}
